import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { FormattedMessage } from 'react-intl';
import Pagination from 'react-js-pagination';

import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';
import ResponsiveSidebarFilters from '../../Components/Utils/ResponsiveSidebarFilters';
import AdminFiltersToggle from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle';
import DateRange from '../../Components/Utils/DateRange';
import EmptyCard from '../../hoc/EmptyCard';
import Search from '../../Components/Utils/SearchTags';
import DisputesList from '../../Components/ComponentsOfAdministrator/DisputesList';
import DisputeFilters from '../../Components/ComponentsOfAdministrator/DisputeFilters';
import DisputeDetails from '../../Components/ComponentsOfAdministrator/DisputeDetails';

import classes from './styles.module.css';
import filtersClasses from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle/styles.module.css';

const DisputesPage = withAuth(function ({ token }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const [totalCount, setTotalCount] = useState(0);
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [stats, setStats] = useState(null);
    const [filterDate, setFilterDate] = useState({});
    const [filters, setFilters] = useState({ filters: [] });
    const [searchTags, setSearchTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    useEffect(() => {
        const statsPayload = {
            filters: filters.filters || [],
            dateRange: filterDate,
        };

        if (filters.merchantId) {
            statsPayload.merchantId = filters.merchantId;
        }

        new Api({ token })
            .getDisputeStats(statsPayload)
            .then((r) => {
                setStats(r || null);
            })
            .catch((err) => {
                Sentry.captureException(err);
                setStats(null);
            });
    }, [token, filters, filterDate]);

    useEffect(() => {
        const payload = {
            page: currentPage,
            itemsPerPage: perPage,
            dateRange: filterDate,
            filters: filters.filters || [],
        };

        if (filters.merchantId) {
            payload.merchantId = filters.merchantId;
        }

        searchTags.forEach((tag) => {
            if (tag.transactionId) payload.transactionId = tag.transactionId;
            if (tag.merchantName) payload.merchantName = tag.merchantName;
            if (tag.senderName) payload.senderName = tag.senderName;
            if (tag.messageText) payload.messageText = tag.messageText;
            if (tag.uuidOrOrderId) payload.uuidOrOrderId = tag.uuidOrOrderId;
        });

        setLoading(true);
        new Api({ token })
            .getDisputesList(payload)
            .then((r) => {
                setList(Array.isArray(r.data) ? r.data : []);
                setTotalCount(typeof r.count === 'number' ? r.count : 0);
                setLoading(false);
            })
            .catch((err) => {
                Sentry.captureException(err);
                setList([]);
                setTotalCount(0);
                setLoading(false);
            });
    }, [filters, filterDate, currentPage, perPage, searchTags, token]);

    function setPage(page, itemsPerPage) {
        setCurrentPage(page || 1);
        if (itemsPerPage) setPerPage(itemsPerPage);
    }

    function onChangeSearch(tags) {
        setSearchTags(tags);
        setCurrentPage(1);
    }

    function onSelectDispute(row) {
        if (row && row.id) {
            new Api({ token })
                .getDispute(row.id)
                .then((full) => {
                    setSelected(full || row);
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    setSelected(row);
                });
        } else {
            setSelected(null);
        }
    }

    function onDisputeUpdate(updatedDispute) {
        setSelected(updatedDispute);
        setList((prev) => prev.map((d) => (d.id === updatedDispute.id ? { ...d, ...updatedDispute } : d)));

        const statsPayload = {
            filters: filters.filters || [],
            dateRange: filterDate,
        };

        if (filters.merchantId) {
            statsPayload.merchantId = filters.merchantId;
        }

        new Api({ token })
            .getDisputeStats(statsPayload)
            .then((r) => {
                setStats(r || null);
            })
            .catch((err) => {
                Sentry.captureException(err);
                setStats(null);
            });
    }

    function onApplyFilters(newFilters) {
        setFilters(newFilters || { filters: [] });
        setCurrentPage(1);
    }

    return (
        <Wrapper>
            <Container>
                <Column width="780" className={filtersClasses.MainColumn}>
                    <AdminFiltersToggle onClick={() => setIsFiltersOpen(true)} />
                    <Search
                        onChangeSearch={onChangeSearch}
                        onSearch={() => {
                            setSearchTags((prev) => [...prev]);
                            setCurrentPage(1);
                        }}
                        isDisabled={false}
                        defaultSearch={{
                            key: 'transactionId',
                            locale: 'Search.label.transactionId',
                            placeholder: 'Search.placeholder.transactionId',
                        }}
                        searchBy={[
                            {
                                key: 'transactionId',
                                locale: 'Search.label.transactionId',
                                placeholder: 'Search.placeholder.transactionId',
                            },
                            {
                                key: 'merchantName',
                                locale: 'Search.label.merchantName',
                                placeholder: 'Search.placeholder.merchantName',
                            },
                            {
                                key: 'senderName',
                                locale: 'Search.label.senderName',
                                placeholder: 'Search.placeholder.senderName',
                            },
                            {
                                key: 'messageText',
                                locale: 'Search.label.messageText',
                                placeholder: 'Search.placeholder.messageText',
                            },
                            {
                                key: 'uuidOrOrderId',
                                locale: 'Search.label.uuidOrOrderId',
                                placeholder: 'Search.placeholder.uuidOrOrderId',
                            },
                        ]}
                    />

                    <div className={classes.Card}>
                        <div className={classes.Header}>
                            <h5 className={classes.Title}>
                                <FormattedMessage id="Disputes.title" defaultMessage="Диспуты" />
                                {stats && <span className={classes.StatsInfo}>({stats.total || 0} активных)</span>}
                            </h5>
                        </div>

                        <DisputesList
                            isLoading={loading}
                            data={list}
                            currentId={selected ? selected.id : null}
                            onSelect={onSelectDispute}
                        />

                        {perPage < totalCount && (
                            <div className={classes.PaginationWrap}>
                                <Pagination
                                    firstPageText="⇤"
                                    prevPageText="⇠"
                                    lastPageText="⇥"
                                    nextPageText="⇢"
                                    activeLinkClass={classes.ActiveLink}
                                    activePage={currentPage}
                                    itemsCountPerPage={perPage}
                                    totalItemsCount={totalCount}
                                    pageRangeDisplayed={5}
                                    onChange={(page) => setPage(page, perPage)}
                                />
                            </div>
                        )}
                    </div>

                    {selected && (
                        <DisputeDetails
                            dispute={selected}
                            token={token}
                            onUpdate={onDisputeUpdate}
                            onClose={() => setSelected(null)}
                        />
                    )}
                </Column>

                <Column width="350" className={filtersClasses.SidebarColumn}>
                    <ResponsiveSidebarFilters
                        title="Фильтры"
                        isOpen={isFiltersOpen}
                        onClose={() => setIsFiltersOpen(false)}
                    >
                        <EmptyCard>
                            <DateRange
                                onSelectDate={(range) => {
                                    setFilterDate(range || {});
                                    setCurrentPage(1);
                                }}
                                defaultPeriod={'zero'}
                            />
                        </EmptyCard>

                        <DisputeFilters
                            isLoading={loading}
                            onApplyFilters={(newFilters) => {
                                onApplyFilters(newFilters);
                                setIsFiltersOpen(false);
                            }}
                            token={token}
                        />

                        {stats && (
                            <div className={classes.StatsCard}>
                                <h6 className={classes.StatsTitle}>
                                    <FormattedMessage id="Disputes.stats.title" defaultMessage="Статистика" />
                                </h6>
                                <div className={classes.StatsList}>
                                    <div className={classes.StatsItem}>
                                        <span>Новых:</span>
                                        <strong className={classes.StatsNew}>{stats.new || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>В работе:</span>
                                        <strong className={classes.StatsProgress}>{stats.in_progress || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>Решённых:</span>
                                        <strong className={classes.StatsResolved}>{stats.resolved || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>В архиве:</span>
                                        <strong className={classes.StatsArchived}>{stats.archived || 0}</strong>
                                    </div>
                                    <div className={classes.StatsDivider} />
                                    <div className={classes.StatsItem}>
                                        <span>Сегодня:</span>
                                        <strong>{stats.today || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>Всего активных:</span>
                                        <strong>{stats.total || 0}</strong>
                                    </div>
                                </div>
                            </div>
                        )}
                    </ResponsiveSidebarFilters>
                </Column>
            </Container>
        </Wrapper>
    );
});

export default DisputesPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Disputes/index.js